import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { blocksNavigation } from './unsaved-changes.model';

// ─── Avisar antes de perder cambios sin guardar ─────────────────────────────
//
// Dos caminos para perderlos, y los dos se cubren:
//   - salir del sitio o recargar → `beforeunload` (el navegador muestra su
//     propio aviso; el texto no se puede elegir);
//   - ir a otra pantalla DE LA APP (un enlace, `navigate`, el botón Atrás) →
//     el bloqueo del router (`useBlocker`), con un diálogo propio.

export function UnsavedChangesGuard({
  when,
  title,
  body,
}: {
  when: boolean;
  title: string;
  body: string;
}): JSX.Element {
  const { t } = useTranslation('common');

  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Algunos navegadores todavía piden el valor para mostrar el aviso.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    blocksNavigation(when, currentLocation, nextLocation),
  );

  return (
    <Dialog
      open={blocker.state === 'blocked'}
      onClose={() => blocker.reset?.()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{body}</Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => blocker.reset?.()}>
          {t('unsavedChanges.stay')}
        </Button>
        <Button color="inherit" onClick={() => blocker.proceed?.()}>
          {t('unsavedChanges.leave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
